import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { admiOnsiteVisitService } from './onsite-visit.service';
import { GeoLocation, LocationSearch } from 'src/features/real-estate/buildings.dto';
import {
    AttachmentDto,
    Building,
    OnsiteVisitTemplateDto,
    RequestStatus,
    FormResponseDto,
    FormSubmitPayloadDto,
} from './onsite-visit.dto';
import { admiBuildingsService } from 'src/features/real-estate/admi-buildings.service';

// Polymorphic form thunks
export const loadPolymorphicForm = createAsyncThunk<FormResponseDto, string>(
    'onsiteVisit/loadPolymorphicForm',
    async (externalId, { rejectWithValue }) => {
        try {
            const response = await admiOnsiteVisitService.getPolymorphicFormQuestions(externalId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load form');
        }
    },
);

export const submitPolymorphicForm = createAsyncThunk<
    void,
    { visitId: string; payload: FormSubmitPayloadDto },
    { rejectValue: string }
>('onsiteVisit/submitPolymorphicForm', async ({ visitId, payload }, { rejectWithValue }) => {
    try {
        await admiOnsiteVisitService.submitPolymorphicForm(visitId, payload);
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to submit form');
    }
});

export const loadRealEstateData = createAsyncThunk(
    'onsiteVisit/loadRealEstateData',
    async (dataSearch: LocationSearch): Promise<Building[]> => {
        const response = await admiBuildingsService.loadBuildingsData(dataSearch);

        return response.buildings.map(({ name, code, geometry, id, dataProductId }) => ({
            name,
            code,
            geometry,
            id,
            dataProductId,
            roof: null,
        }));
    },
);

// Removed roof segment related thunks

export const loadOnsiteTemplates = createAsyncThunk<OnsiteVisitTemplateDto[], void>(
    'onsiteVisit/loadOnsiteTemplates',
    async (_, { rejectWithValue }) => {
        try {
            const response = await admiOnsiteVisitService.getOnsiteTemplates();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load onsite templates');
        }
    },
);

export const uploadAttachment = createAsyncThunk<
    { questionId: string; attachment: AttachmentDto & { isTmp: boolean } },
    { file: File; questionId: string }
>('onsiteVisit/uploadAttachment', async ({ file, questionId }) => {
    const result = await admiOnsiteVisitService.uploadAttachment(file, questionId);

    return {
        questionId,
        attachment: { ...result, isTmp: true },
    };
});

export interface State {
    location: GeoLocation | null;
    errorMessage: string;
    successMessage: string;
    buildings: Building[];
    selectedBuildings: Building[] | null;
    isMapLoading: boolean;
    polymorphicFormData: FormResponseDto | null;
    highlightedBuilding: string | null;
    submissionStatus: RequestStatus | null;
    templates: OnsiteVisitTemplateDto[];
    uploadedAttachments: Record<string, AttachmentDto[]>;
    uploadStatus: Record<string, RequestStatus>;
    removedAttachments: Record<string, string[]>;
    // Removed roof segment status fields
}

const initialState: State = {
    location: {
        address: 'Oberbilker Allee 244, 40227 Düsseldorf',
        lat: 51.2108431,
        lng: 6.802931999999999,
    },
    errorMessage: '',
    successMessage: '',
    buildings: [],
    selectedBuildings: null,
    isMapLoading: false,
    polymorphicFormData: null,
    highlightedBuilding: null,
    submissionStatus: null,
    templates: [],
    uploadedAttachments: {},
    uploadStatus: {},
    removedAttachments: {},
    // Removed roof segment status fields
};

const onsiteVisitSlice = createSlice({
    name: 'onsiteVisit',
    initialState,
    reducers: {
        setLocation(state, action: PayloadAction<GeoLocation>) {
            state.location = action.payload;
        },
        setErrorMessage(state, action: PayloadAction<string>) {
            state.errorMessage = action.payload;
        },
        setSuccessMessage(state, action: PayloadAction<string>) {
            state.successMessage = action.payload;
        },
        setSelectedBuildings(state, action: PayloadAction<Building[] | null>) {
            state.selectedBuildings = action.payload;
        },
        setIsMapLoading(state, action: PayloadAction<boolean>) {
            state.isMapLoading = action.payload;
        },
        resetBuildingSelection(state) {
            state.buildings = [];
            state.selectedBuildings = null;
        },
        setHighlightedBuilding: (state, action: PayloadAction<string | null>) => {
            state.highlightedBuilding = action.payload;
        },
        setSubmissionStatus: (state, action: PayloadAction<RequestStatus | null>) => {
            state.submissionStatus = action.payload;
        },
        removeAttachment(state, action: PayloadAction<{ questionId: string; externalId: string }>) {
            const { questionId, externalId } = action.payload;
            if (!state.removedAttachments[questionId]) {
                state.removedAttachments[questionId] = [];
            }
            state.removedAttachments[questionId].push(externalId);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadPolymorphicForm.fulfilled, (state, action: PayloadAction<FormResponseDto>) => {
                state.polymorphicFormData = action.payload;
            })
            .addCase(loadPolymorphicForm.rejected, (state, action) => {
                state.errorMessage = action.payload as string;
            })
            .addCase(loadRealEstateData.pending, (state) => {
                state.isMapLoading = true;
            })
            .addCase(loadRealEstateData.fulfilled, (state, action) => {
                state.isMapLoading = false;
                state.buildings = action.payload;
            })
            .addCase(loadRealEstateData.rejected, (state, action) => {
                state.isMapLoading = false;
                state.errorMessage = action.error.message ?? action.error.code ?? 'Unknown error';
            })
            .addCase(submitPolymorphicForm.pending, (state) => {
                state.submissionStatus = {
                    isLoading: true,
                    message: null,
                    type: null,
                };
            })
            .addCase(submitPolymorphicForm.fulfilled, (state) => {
                state.isMapLoading = false;
                state.submissionStatus = {
                    isLoading: false,
                    message: 'Form submitted successfully!',
                    type: 'success',
                };
            })
            .addCase(submitPolymorphicForm.rejected, (state, action) => {
                state.isMapLoading = false;
                state.submissionStatus = {
                    isLoading: false,
                    message: action.payload as string,
                    type: 'error',
                };
            })
            .addCase(loadOnsiteTemplates.pending, (state) => {
                state.templates = [];
            })
            .addCase(loadOnsiteTemplates.fulfilled, (state, action: PayloadAction<OnsiteVisitTemplateDto[]>) => {
                state.templates = action.payload;
            })
            .addCase(loadOnsiteTemplates.rejected, (state, action) => {
                state.errorMessage = action.payload as string;
            })
            .addCase(uploadAttachment.pending, (state, action) => {
                const questionId = action.meta.arg.questionId;
                state.uploadStatus[questionId] = {
                    isLoading: true,
                    message: null,
                    type: null,
                };
            })
            .addCase(uploadAttachment.fulfilled, (state, action) => {
                const { questionId, attachment } = action.payload;

                if (!state.uploadedAttachments[questionId]) {
                    state.uploadedAttachments[questionId] = [];
                }

                state.uploadedAttachments[questionId].push(attachment);

                state.uploadStatus[questionId] = {
                    isLoading: false,
                    message: 'Upload successful',
                    type: 'success',
                };
            })
            .addCase(uploadAttachment.rejected, (state, action) => {
                const questionId = action.meta.arg.questionId;
                state.uploadStatus[questionId] = {
                    isLoading: false,
                    message: action.error.message || 'Upload failed',
                    type: 'error',
                };
            });
        // Removed roof segment related extra reducers
    },
});

export const {
    setLocation,
    setErrorMessage,
    setSuccessMessage,
    setSelectedBuildings,
    setIsMapLoading,
    resetBuildingSelection,
    setHighlightedBuilding,
    setSubmissionStatus,
    removeAttachment,
} = onsiteVisitSlice.actions;

export default onsiteVisitSlice.reducer;
